/**
 * Ideas Kitchen - Exploratory Intelligence Tab
 *
 * Fast, non-deterministic exploratory workspace powered by Google Vertex AI / Gemini APIs.
 * Designed for discovery and drafting — NOT verification or final judgment.
 *
 * Features:
 * - Document ingestion & indexing (Google managed)
 * - Grounded Q&A with citations
 * - Corpus/Topic summarization
 * - Static audio overview (batch MP3)
 * - Citation highlighting & navigation
 * - Exploratory guardrails (non-authoritative labels)
 *
 * @module IdeasKitchenPage
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';

import {
  Upload,
  MessageSquare,
  FileText,
  Headphones,
  Sparkles,
  AlertTriangle,
  Loader2,
  Plus,
  Search,
  Copy,
  Download,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Trash2,
  MoreVertical,
  X,
  Check,
  Play,
  Pause,
  Volume2,
  Clock,
  Users,
  Zap,
  Brain,
  BookOpen,
  FileUp,
  Send,
  ArrowRight,
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

// API client
import ideasKitchenApi from '@/pages/ideas-kitchen/api/ideasKitchenApi';

// Sub-components
import AudioOverviewPanel from '@/pages/ideas-kitchen/components/AudioOverviewPanel';
import ChatPanel from '@/pages/ideas-kitchen/components/ChatPanel';
import CitationHighlighter from '@/pages/ideas-kitchen/components/CitationHighlighter';
import CreateNotebookModal from '@/pages/ideas-kitchen/components/CreateNotebookModal';
import ExploratoryBanner from '@/pages/ideas-kitchen/components/ExploratoryBanner';
import NotebookCard from '@/pages/ideas-kitchen/components/NotebookCard';
import SourcesPanel from '@/pages/ideas-kitchen/components/SourcesPanel';
import SummaryPanel from '@/pages/ideas-kitchen/components/SummaryPanel';
import ideasKitchenLogo from '../../assets/ideas-kitchen-logo.png';
import { FEATURE_FLAGS } from '../../config/featureFlags';

// ============================================================
// MAIN IDEAS KITCHEN PAGE
// ============================================================

const IdeasKitchenPage = () => {
  // URL parameters for persistence
  const [searchParams, setSearchParams] = useSearchParams();
  const notebookIdFromUrl = searchParams.get('id');

  // Core state
  const [notebooks, setNotebooks] = useState([]);
  const [selectedNotebook, setSelectedNotebook] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quotaStatus, setQuotaStatus] = useState(null);

  // View state
  const [view, setView] = useState(notebookIdFromUrl ? 'notebook' : 'home');
  const [activeTab, setActiveTab] = useState('chat'); // chat | summary | audio

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Panel state
  const [leftPanelWidth, setLeftPanelWidth] = useState(280);
  const [rightPanelWidth, setRightPanelWidth] = useState(320);
  const [isLeftCollapsed, setIsLeftCollapsed] = useState(false);
  const [isRightCollapsed, setIsRightCollapsed] = useState(true);

  // Selected file for preview
  const [selectedFile, setSelectedFile] = useState(null);
  const [highlightedCitation, setHighlightedCitation] = useState(null);
  const canDeleteFile = FEATURE_FLAGS.IDEAS_KITCHEN_DELETE_ENABLED === true;
  const quotaEnabled = FEATURE_FLAGS.IDEAS_KITCHEN_QUOTA_ENABLED === true;

  const mergeNotebookDetails = useCallback(
    (previousNotebook, notebookPayload, sourcesPayload, chatHistoryPayload) => {
      const incomingNotebook = notebookPayload || {};
      const incomingId =
        incomingNotebook?.id ||
        incomingNotebook?.notebookId ||
        previousNotebook?.id ||
        previousNotebook?.notebookId ||
        null;
      const previousId = previousNotebook?.id || previousNotebook?.notebookId || null;
      const sameNotebook =
        Boolean(incomingId) && Boolean(previousId) ? String(incomingId) === String(previousId) : false;

      const previousSources =
        sameNotebook && Array.isArray(previousNotebook?.sources) ? previousNotebook.sources : [];
      const previousChatHistory =
        sameNotebook && Array.isArray(previousNotebook?.chatHistory)
          ? previousNotebook.chatHistory
          : [];

      const incomingSources = Array.isArray(sourcesPayload) ? sourcesPayload : [];
      const incomingChatHistory = Array.isArray(chatHistoryPayload) ? chatHistoryPayload : [];

      return {
        ...(sameNotebook ? previousNotebook : {}),
        ...incomingNotebook,
        id: incomingId || incomingNotebook?.id || incomingNotebook?.notebookId || null,
        sources: incomingSources.length > 0 ? incomingSources : previousSources,
        chatHistory: incomingChatHistory.length > 0 ? incomingChatHistory : previousChatHistory,
      };
    },
    []
  );

  // ============================================================
  // EFFECTS
  // ============================================================

  // Fetch notebooks on mount
  useEffect(() => {
    fetchNotebooks();
  }, []);

  // Load notebook from URL
  useEffect(() => {
    const loadNotebookFromUrl = async () => {
      if (notebookIdFromUrl && notebooks.length > 0 && !selectedNotebook) {
        const nb = notebooks.find(n => n.id === notebookIdFromUrl);
        if (nb) {
          // Set basic notebook first for immediate UI feedback, but keep prior sources/chat
          // if this notebook was already hydrated to avoid flicker-to-empty.
          setSelectedNotebook(prev => mergeNotebookDetails(prev, nb, nb?.sources, nb?.chatHistory));
          setView('notebook');

          // Then fetch full notebook with sources and chat history
          try {
            const response = await ideasKitchenApi.getNotebook(notebookIdFromUrl);
            if (response.success) {
              setSelectedNotebook(prev =>
                mergeNotebookDetails(prev, response.notebook, response.sources, response.chatHistory)
              );
            }
          } catch (err) {
            console.error('Failed to load notebook from URL:', err);
          }
        }
      }
    };

    loadNotebookFromUrl();
  }, [notebookIdFromUrl, notebooks, selectedNotebook, mergeNotebookDetails]);

  useEffect(() => {
    const loadQuotaStatus = async () => {
      if (!quotaEnabled || !selectedNotebook?.id) return;
      try {
        const res = await ideasKitchenApi.getQuotaStatus(selectedNotebook.id);
        setQuotaStatus(res);
      } catch (err) {
        console.warn('Failed to load quota status:', err);
      }
    };
    loadQuotaStatus();
  }, [quotaEnabled, selectedNotebook?.id]);

  // ============================================================
  // API HANDLERS
  // ============================================================

  const fetchNotebooks = async () => {
    setIsLoading(true);
    try {
      const response = await ideasKitchenApi.listNotebooks();
      if (response.success) {
        setNotebooks(response.notebooks || []);
      }
    } catch (err) {
      console.error('Failed to fetch notebooks:', err);
      setError(err?.message || 'Failed to load notebooks');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateNotebook = async (title, description, files) => {
    setError(null);
    try {
      // Backend expects 'name' not 'title'
      const response = await ideasKitchenApi.createNotebook({
        name: title,
        description,
      });

      const rawNotebook =
        response?.notebook || response?.data?.notebook || response?.data || response || null;
      const notebookId = rawNotebook?.id || rawNotebook?.notebookId || null;
      if (!notebookId) {
        throw new Error('No notebook ID returned from create response');
      }

      let notebookForView = {
        ...rawNotebook,
        id: notebookId,
      };

      // Upload initial files if provided (async - returns 202)
      if (files && files.length > 0) {
        const uploadResponse = await ideasKitchenApi.uploadFiles(notebookId, files);
        const acceptedSources = Array.isArray(uploadResponse?.sources)
          ? uploadResponse.sources
          : Array.isArray(uploadResponse?.data?.sources)
            ? uploadResponse.data.sources
            : [];
        if (acceptedSources.length > 0) {
          notebookForView = {
            ...notebookForView,
            sources: acceptedSources,
          };
        }

        // Wait a moment for processing to start, then refresh
        await new Promise(r => setTimeout(r, 1000));
        const refreshed = await ideasKitchenApi.getNotebook(notebookId);
        const refreshedNotebook = refreshed?.notebook || refreshed?.data?.notebook || null;
        const refreshedSources = Array.isArray(refreshed?.sources)
          ? refreshed.sources
          : Array.isArray(refreshed?.data?.sources)
            ? refreshed.data.sources
            : [];
        const refreshedChatHistory = Array.isArray(refreshed?.chatHistory)
          ? refreshed.chatHistory
          : Array.isArray(refreshed?.data?.chatHistory)
            ? refreshed.data.chatHistory
            : [];
        if ((refreshed?.success || refreshed?.data?.success) && refreshedNotebook) {
          notebookForView = mergeNotebookDetails(
            notebookForView,
            refreshedNotebook,
            refreshedSources,
            refreshedChatHistory
          );
        }

        // If notebook read is eventually consistent, fetch source status directly.
        if (!Array.isArray(notebookForView.sources) || notebookForView.sources.length === 0) {
          const status = await ideasKitchenApi.getSourcesStatus(notebookId);
          const statusSources = Array.isArray(status?.sources)
            ? status.sources
            : Array.isArray(status?.data?.sources)
              ? status.data.sources
              : [];
          if (statusSources.length > 0) {
            notebookForView = mergeNotebookDetails(
              notebookForView,
              notebookForView,
              statusSources,
              notebookForView.chatHistory
            );
          }
        }
      }

      setSelectedNotebook(notebookForView);
      setNotebooks(prev => {
        const exists = prev.some(n => (n?.id || n?.notebookId) === notebookId);
        if (exists) {
          return prev.map(n =>
            (n?.id || n?.notebookId) === notebookId ? { ...n, ...notebookForView } : n
          );
        }
        return [...prev, notebookForView];
      });
      setShowCreateModal(false);
      setView('notebook');
      setSearchParams({ id: notebookId });

      return notebookForView;
    } catch (err) {
      console.error('Failed to create notebook:', err);
      const message = err?.message || 'Failed to create notebook';
      setError(message);
      throw err;
    }
  };

  const handleSelectNotebook = async notebook => {
    // First set the basic notebook to show immediate UI feedback,
    // preserving existing hydrated sources/chat for the same notebook.
    setSelectedNotebook(prev =>
      mergeNotebookDetails(prev, notebook, notebook?.sources, notebook?.chatHistory)
    );
    setView('notebook');
    setSearchParams({ id: notebook.id });

    // Then fetch the full notebook with sources and chat history
    try {
      const response = await ideasKitchenApi.getNotebook(notebook.id);
      if (response.success) {
        setSelectedNotebook(prev =>
          mergeNotebookDetails(prev, response.notebook, response.sources, response.chatHistory)
        );
      }
    } catch (err) {
      console.error('Failed to load notebook details:', err);
      setError('Failed to load notebook sources');
    }
  };

  const handleBackToHome = () => {
    setSelectedNotebook(null);
    setView('home');
    setSearchParams({});
    setSelectedFile(null);
    setHighlightedCitation(null);
  };

  const handleDeleteNotebook = async notebookId => {
    try {
      await ideasKitchenApi.deleteNotebook(notebookId);
      setNotebooks(prev => prev.filter(n => n.id !== notebookId));
      if (selectedNotebook?.id === notebookId) {
        handleBackToHome();
      }
    } catch (err) {
      console.error('Failed to delete notebook:', err);
      setError('Failed to delete notebook');
    }
  };

  const handleRefreshNotebook = async () => {
    if (!selectedNotebook) return;
    try {
      const response = await ideasKitchenApi.getNotebook(selectedNotebook.id);
      if (response.success) {
        setSelectedNotebook(prev =>
          mergeNotebookDetails(prev, response.notebook, response.sources, response.chatHistory)
        );
      }
    } catch (err) {
      console.error('Failed to refresh notebook:', err);
    }
  };

  const handleCitationClick = citation => {
    setHighlightedCitation(citation);
    if (citation.sourceId && selectedNotebook?.sources) {
      const source = selectedNotebook.sources.find(s => s.id === citation.sourceId);
      if (source) {
        setSelectedFile(source);
        setIsRightCollapsed(false);
      }
    }
  };

  // ============================================================
  // RENDER: HOME VIEW
  // ============================================================

  if (view === 'home') {
    const filteredNotebooks = notebooks.filter(
      n =>
        n.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        n.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        n.displayName?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
      <div className="h-screen w-full flex flex-col bg-gradient-to-br from-slate-50 via-amber-50/30 to-orange-50/20 dark:from-neutral-900 dark:via-neutral-900 dark:to-neutral-800 overflow-hidden">
        {/* Exploratory Banner */}
        <ExploratoryBanner />

        {/* Header */}
        <div className="flex-shrink-0 px-6 py-5 border-b border-slate-200/80 dark:border-neutral-700/80 bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-4">
              <div className="h-14 w-14 flex items-center justify-center">
                <img
                  src={ideasKitchenLogo}
                  alt="Ideas Kitchen"
                  className="h-14 w-14 object-contain"
                  draggable={false}
                />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                  Ideas{' '}
                  <span className="bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-transparent">
                    Kitchen
                  </span>
                  <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 rounded-full">
                    Exploratory
                  </span>
                </h1>
                <p className="text-sm text-slate-500 dark:text-neutral-400 mt-0.5">
                  Fast exploratory intelligence for discovery & drafting — not verification
                </p>
              </div>
            </div>

            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all shadow-lg shadow-amber-500/25 font-medium"
            >
              <Plus className="h-5 w-5" />
              <span>New Kitchen</span>
            </button>
          </div>
        </div>

        {/* Search & Stats */}
        <div className="flex-shrink-0 px-6 py-4 border-b border-slate-200/60 dark:border-neutral-700/60 bg-white/60 dark:bg-neutral-900/60 backdrop-blur-sm">
          <div className="w-full flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="relative flex-1 min-w-[300px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search kitchens..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-slate-100 dark:bg-neutral-800 border-0 rounded-xl text-sm focus:ring-2 focus:ring-amber-500 transition-all"
                />
              </div>
              <div className="text-sm text-slate-500 dark:text-neutral-400 flex items-center gap-2">
                <BookOpen className="h-4 w-4" />
                {notebooks.length} kitchen{notebooks.length !== 1 ? 's' : ''}
              </div>
            </div>
          </div>
        </div>

        {/* Notebooks Grid */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="w-full">
            {isLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="flex flex-col items-center gap-4">
                  <Loader2 className="h-10 w-10 animate-spin text-amber-500" />
                  <p className="text-slate-500 dark:text-neutral-400">Loading your kitchens...</p>
                </div>
              </div>
            ) : filteredNotebooks.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 px-6">
                <div className="h-20 w-20 flex items-center justify-center mb-4">
                  <img
                    src={ideasKitchenLogo}
                    alt="Ideas Kitchen"
                    className="h-20 w-20 object-contain"
                    draggable={false}
                  />
                </div>
                <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-2">
                  {searchQuery ? 'No kitchens found' : 'Start your first Ideas Kitchen'}
                </h3>
                <p className="text-sm text-slate-500 dark:text-neutral-400 text-center max-w-md mb-6">
                  {searchQuery
                    ? `No kitchens matching "${searchQuery}"`
                    : 'Upload documents and let AI help you explore, discover patterns, and generate fast insights.'}
                </p>
                {!searchQuery && (
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all shadow-lg shadow-amber-500/25 font-medium"
                  >
                    <Plus className="h-5 w-5" />
                    <span>Create Kitchen</span>
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {filteredNotebooks.map(notebook => (
                  <NotebookCard
                    key={notebook.id}
                    notebook={notebook}
                    onSelect={() => handleSelectNotebook(notebook)}
                    onDelete={() => handleDeleteNotebook(notebook.id)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Create Modal */}
        {showCreateModal && (
          <CreateNotebookModal
            onClose={() => setShowCreateModal(false)}
            onCreate={handleCreateNotebook}
          />
        )}

        {/* Error Toast */}
        {error && (
          <div className="fixed bottom-4 right-4 bg-red-500 text-white px-4 py-3 rounded-xl shadow-lg flex items-center gap-3 animate-slide-up">
            <AlertTriangle className="h-5 w-5" />
            <span>{error}</span>
            <button onClick={() => setError(null)} className="hover:bg-red-600 p-1 rounded">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    );
  }

  // ============================================================
  // RENDER: NOTEBOOK VIEW
  // ============================================================

  return (
    <div className="h-screen w-full flex flex-col bg-slate-50 dark:bg-neutral-900 overflow-hidden">
      {/* Exploratory Banner - Always visible */}
      <ExploratoryBanner />

      {/* Notebook Header */}
      <div className="flex-shrink-0 px-4 py-3 border-b border-slate-200 dark:border-neutral-700 bg-white dark:bg-neutral-900">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBackToHome}
              className="p-2 hover:bg-slate-100 dark:hover:bg-neutral-800 rounded-lg transition-colors"
              title="Back to kitchens"
            >
              <ChevronLeft className="h-5 w-5 text-slate-600 dark:text-neutral-400" />
            </button>
            <div className="flex items-center gap-2">
              <div className="h-9 w-9 flex items-center justify-center">
                <img
                  src={ideasKitchenLogo}
                  alt="Ideas Kitchen"
                  className="h-9 w-9 object-contain"
                  draggable={false}
                />
              </div>
              <div>
                <h2 className="font-semibold text-slate-800 dark:text-white">
                  {selectedNotebook?.name ||
                    selectedNotebook?.title ||
                    selectedNotebook?.displayName ||
                    'Untitled Kitchen'}
                </h2>
                <p className="text-xs text-slate-500 dark:text-neutral-400">
                  {selectedNotebook?.sources?.length || 0} sources • Created{' '}
                  {selectedNotebook?.createdAt
                    ? new Date(selectedNotebook.createdAt).toLocaleDateString()
                    : 'recently'}
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleRefreshNotebook}
              className="p-2 hover:bg-slate-100 dark:hover:bg-neutral-800 rounded-lg transition-colors"
              title="Refresh"
            >
              <RefreshCw className="h-5 w-5 text-slate-600 dark:text-neutral-400" />
            </button>
          </div>
        </div>

        {selectedNotebook?.status === 'partial_ready' && selectedNotebook?.missingSourcesCount > 0 && (
          <div className="mt-3 flex items-center gap-2 px-3 py-2 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg text-xs text-amber-700 dark:text-amber-300">
            <AlertTriangle className="h-4 w-4" />
            <span>
              {selectedNotebook.missingSourcesCount} source
              {selectedNotebook.missingSourcesCount === 1 ? '' : 's'} failed to process. You can
              retry from the Sources panel.
            </span>
          </div>
        )}

        {quotaEnabled && quotaStatus?.core?.ok && (() => {
          const usage = quotaStatus.core.usage || {};
          const window = quotaStatus.core.window || {};
          const jobsLimit = Number(usage.dailyJobsLimit || 0);
          const jobsUsed = Number(usage.dailyJobsUsed || 0);
          const costLimit = Number(usage.dailyCostLimit || 0);
          const costUsed = Number(usage.dailyCostUsed || 0);
          const jobsBlocked = jobsLimit > 0 && jobsUsed >= jobsLimit;
          const costBlocked = costLimit > 0 && costUsed >= costLimit;
          if (!jobsBlocked && !costBlocked) return null;
          return (
            <div className="mt-3 flex items-center gap-2 px-3 py-2 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-xs text-red-700 dark:text-red-300">
              <AlertTriangle className="h-4 w-4" />
              <span>
                Ideas Kitchen usage limit reached. Resets {window?.resetsAt ? `at ${new Date(window.resetsAt).toLocaleString()}` : 'soon'}.
              </span>
            </div>
          );
        })()}

        {/* Tab Navigation */}
        <div className="flex items-center gap-1 mt-3">
          {[
            { id: 'chat', label: 'Ask', icon: MessageSquare },
            { id: 'summary', label: 'Summarize', icon: FileText },
            { id: 'audio', label: 'Audio Overview', icon: Headphones },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                  : 'text-slate-600 dark:text-neutral-400 hover:bg-slate-100 dark:hover:bg-neutral-800'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content Area - 3 Panel Layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Sources */}
        <div
          className={`flex-shrink-0 border-r border-slate-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 transition-all duration-200 ${
            isLeftCollapsed ? 'w-0 overflow-hidden' : ''
          }`}
          style={{ width: isLeftCollapsed ? 0 : leftPanelWidth }}
        >
          {!isLeftCollapsed && (
            <SourcesPanel
              notebook={selectedNotebook}
              onFileSelect={file => {
                setSelectedFile(file);
                setIsRightCollapsed(false);
              }}
              selectedFile={selectedFile}
              onDelete={async fileId => {
                if (!selectedNotebook) return;
                if (!canDeleteFile) {
                  setError('File deletion is disabled for this environment or plan.');
                  return;
                }
                try {
                  await ideasKitchenApi.deleteFile(selectedNotebook.id, fileId);
                  handleRefreshNotebook();
                } catch (err) {
                  console.error('Failed to delete file:', err);
                  setError(err?.message || 'Failed to delete file');
                }
              }}
              onRefresh={handleRefreshNotebook}
            />
          )}
        </div>

        {/* Toggle Left Panel */}
        <button
          onClick={() => setIsLeftCollapsed(!isLeftCollapsed)}
          className="flex-shrink-0 w-5 flex items-center justify-center hover:bg-slate-100 dark:hover:bg-neutral-800 transition-colors"
        >
          {isLeftCollapsed ? (
            <ChevronRight className="h-4 w-4 text-slate-400" />
          ) : (
            <ChevronLeft className="h-4 w-4 text-slate-400" />
          )}
        </button>

        {/* Center Panel - Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden bg-slate-50 dark:bg-neutral-950">
          {activeTab === 'chat' && (
            <ChatPanel notebook={selectedNotebook} onCitationClick={handleCitationClick} />
          )}
          {activeTab === 'summary' && <SummaryPanel notebook={selectedNotebook} />}
          {activeTab === 'audio' && (
            <AudioOverviewPanel notebook={selectedNotebook} quotaStatus={quotaStatus?.audio} />
          )}
        </div>

        {/* Toggle Right Panel */}
        <button
          onClick={() => setIsRightCollapsed(!isRightCollapsed)}
          className="flex-shrink-0 w-5 flex items-center justify-center hover:bg-slate-100 dark:hover:bg-neutral-800 transition-colors"
        >
          {isRightCollapsed ? (
            <ChevronLeft className="h-4 w-4 text-slate-400" />
          ) : (
            <ChevronRight className="h-4 w-4 text-slate-400" />
          )}
        </button>

        {/* Right Panel - Preview / Citations */}
        <div
          className={`flex-shrink-0 border-l border-slate-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 transition-all duration-200 ${
            isRightCollapsed ? 'w-0 overflow-hidden' : ''
          }`}
          style={{ width: isRightCollapsed ? 0 : rightPanelWidth }}
        >
          {!isRightCollapsed && selectedFile && (
            <CitationHighlighter
              file={selectedFile}
              citation={highlightedCitation}
              notebookId={selectedNotebook?.id}
              onClose={() => {
                setSelectedFile(null);
                setIsRightCollapsed(true);
              }}
            />
          )}
        </div>
      </div>

      {/* Error Toast */}
      {error && (
        <div className="fixed bottom-4 right-4 bg-red-500 text-white px-4 py-3 rounded-xl shadow-lg flex items-center gap-3 animate-slide-up z-50">
          <AlertTriangle className="h-5 w-5" />
          <span>{error}</span>
          <button onClick={() => setError(null)} className="hover:bg-red-600 p-1 rounded">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default IdeasKitchenPage;
